/**
 * Pagination State Helpers
 *
 * Helper functions to create consistent pagination state structures
 */

/**
 * Create default pagination meta structure
 * @param {Number} perPage - Default items per page
 * @returns {Object} Pagination meta object
 */
export const createPaginationMeta = (perPage = 50) => ({
    current_page: 1,
    from: 1,
    last_page: 1,
    per_page: perPage,
    to: 0,
    total: 0,
});

/**
 * Create paginated data structure with meta
 * @param {Number} perPage - Default items per page
 * @returns {Object} Paginated data object
 */
export const createPaginatedState = (perPage = 50) => ({
    data: [],
    meta: createPaginationMeta(perPage),
    isServerPaginated: true,
});

/**
 * Create filter state structure
 * @param {Object} customFilters - Custom filter fields
 * @param {Number} perPage - Default items per page
 * @returns {Object} Filter state object
 */
export const createFilterState = (customFilters = {}, perPage = 50) => ({
    page: 1,
    per_page: perPage,
    ...customFilters
});

/**
 * Create paginated state with summary
 * @param {Number} perPage - Default items per page
 * @param {Object} summaryDefaults - Default summary values
 * @returns {Object} Paginated data object with summary
 */
export const createPaginatedStateWithSummary = (perPage = 50, summaryDefaults = {}) => ({
    data: [],
    meta: createPaginationMeta(perPage),
    isServerPaginated: true,
    summary: summaryDefaults
});

/**
 * Handle paginated response and update state
 * @param {Object} stateObject - The state object to update (e.g., state.products)
 * @param {Object} response - API response
 * @param {Number} defaultPerPage - Default per page value if no data
 */
export const handlePaginatedResponse = (stateObject, response, defaultPerPage = 50) => {
    if (response && response.data) {
        stateObject.data = response.data;

        if (response.meta && response.links) {
            // Server-side paginated response
            stateObject.meta = response.meta;
            stateObject.isServerPaginated = true;
        } else {
            // No pagination (same-day or small dataset)
            stateObject.meta = {
                current_page: 1,
                from: 1,
                last_page: 1,
                per_page: response.data.length || defaultPerPage,
                to: response.data.length || 0,
                total: response.data.length || 0,
            };
            stateObject.isServerPaginated = false;
        }

        // Handle summary if present
        if (response.summary) {
            stateObject.summary = response.summary;
        }
    } else {
        // Empty/error response
        stateObject.data = [];
        stateObject.meta = createPaginationMeta(defaultPerPage);
        stateObject.isServerPaginated = false;
    }
};